import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import {
  AddRounded,
  WarningAmberRounded,
  DownloadRounded,
  RefreshRounded,
  SearchRounded,
  MoreVertRounded,
  InsightsRounded,
  TroubleshootRounded,
  NotificationsActiveRounded,
  StopCircleRounded,
  DeleteOutlineRounded,
  FactCheckRounded,
} from '@mui/icons-material';
import { useTenantStore } from '../../../stores/tenant.store';
import {
  surveyApi,
  type QuestionHealth,
  type SurveyOverviewDto,
  type SurveyOverviewRowDto,
} from '../../../lib/api/survey.api';
import { SurveyAnalyticsDialog } from './SurveyAnalyticsDialog';
import { SurveyDiagnosticsDialog } from './SurveyDiagnosticsDialog';
import { SurveyLaunchDrawer } from './SurveyLaunchDrawer';
import { downloadCsv } from '../util/csv';
import { errorText } from '../../../lib/error-text';

const HEALTH_COLOR: Record<QuestionHealth, 'success' | 'warning' | 'error' | 'default'> = {
  good: 'success',
  warn: 'warning',
  bad: 'error',
  na: 'default',
};

type SortKey = 'course' | 'title' | 'kind' | 'status' | 'rate' | 'health' | 'window';
type StatusFilter = 'all' | 'open' | 'closed' | 'draft';
type KindFilter = 'all' | 'periodic' | 'end_of_course';

const STATUS_ORDER: Record<string, number> = { open: 0, draft: 1, closed: 2 };
const HEALTH_ORDER: Record<QuestionHealth, number> = { bad: 0, warn: 1, good: 2, na: 3 };

/** The lifecycle-management home: every survey in the period, filterable,
 *  sortable, with per-row actions and a launch drawer. */
export function SurveyManagementPanel(): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const queryClient = useQueryClient();

  const [termId, setTermId] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const [courseFilter, setCourseFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({
    key: 'window',
    dir: 'desc',
  });

  const [launchOpen, setLaunchOpen] = useState(false);
  const [analyticsSurvey, setAnalyticsSurvey] = useState<{
    surveyId: string;
    title: string;
  } | null>(null);
  const [diagSurvey, setDiagSurvey] = useState<{ surveyId: string; title: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SurveyOverviewRowDto | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuRow, setMenuRow] = useState<SurveyOverviewRowDto | null>(null);
  const [snack, setSnack] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  const overviewKey = ['survey-overview', currentTenantId, termId];
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['survey-overview'] });

  const { data, isLoading } = useQuery({
    queryKey: overviewKey,
    queryFn: () =>
      surveyApi.overview({ tenantId: currentTenantId!, ...(termId ? { termId } : {}) }),
    enabled: !!currentTenantId,
  });

  const templatesQ = useQuery({
    queryKey: ['survey-templates', currentTenantId],
    queryFn: () => surveyApi.listTemplates({ tenantId: currentTenantId! }),
    enabled: !!currentTenantId,
  });
  const templates = templatesQ.data?.templates ?? [];

  const backfill = useMutation({
    mutationFn: () => surveyApi.backfillCounters({ tenantId: currentTenantId! }),
    onSuccess: invalidate,
  });
  const remindMutation = useMutation({
    mutationFn: (surveyId: string) => surveyApi.remind({ tenantId: currentTenantId!, surveyId }),
    onSuccess: (r) =>
      setSnack({ severity: 'success', msg: t('surveys.remindDone', { count: r.notified }) }),
    onError: (e) => setSnack({ severity: 'error', msg: errorText(e, 'error') }),
  });
  const closeMutation = useMutation({
    mutationFn: (surveyId: string) => surveyApi.close({ tenantId: currentTenantId!, surveyId }),
    onSuccess: () => {
      invalidate();
      setSnack({ severity: 'success', msg: t('surveys.closeDone') });
    },
    onError: (e) => setSnack({ severity: 'error', msg: errorText(e, 'error') }),
  });
  const deleteMutation = useMutation({
    mutationFn: (surveyId: string) => surveyApi.remove({ tenantId: currentTenantId!, surveyId }),
    onSuccess: (r) => {
      invalidate();
      setDeleteTarget(null);
      setSnack({ severity: 'success', msg: t('surveys.delete.done', { count: r.responseCount }) });
    },
    onError: (e) => setSnack({ severity: 'error', msg: errorText(e, 'error') }),
  });

  const rows = data?.rows ?? [];

  const courseOptions = useMemo(() => {
    const set = new Map<string, string>();
    for (const r of rows) set.set(r.courseName ?? r.sectionId, r.courseName ?? r.sectionId);
    return Array.from(set.values()).sort();
  }, [rows]);

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = rows.filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (kindFilter !== 'all' && r.kind !== kindFilter) return false;
      if (courseFilter !== 'all' && (r.courseName ?? r.sectionId) !== courseFilter) return false;
      if (
        q &&
        !r.title.toLowerCase().includes(q) &&
        !(r.courseName ?? '').toLowerCase().includes(q)
      )
        return false;
      return true;
    });
    const dir = sort.dir === 'asc' ? 1 : -1;
    const cmp = (a: SurveyOverviewRowDto, b: SurveyOverviewRowDto): number => {
      switch (sort.key) {
        case 'course':
          return (a.courseName ?? a.sectionId).localeCompare(b.courseName ?? b.sectionId) * dir;
        case 'title':
          return a.title.localeCompare(b.title) * dir;
        case 'kind':
          return a.kind.localeCompare(b.kind) * dir;
        case 'status':
          return ((STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9)) * dir;
        case 'rate':
          return (a.responseRate - b.responseRate) * dir;
        case 'health':
          return (HEALTH_ORDER[a.health] - HEALTH_ORDER[b.health]) * dir;
        case 'window':
        default:
          return (a.opensAt ?? '').localeCompare(b.opensAt ?? '') * dir;
      }
    };
    return filtered.slice().sort(cmp);
  }, [rows, statusFilter, kindFilter, courseFilter, search, sort]);

  const openCount = rows.filter((r) => r.status === 'open').length;

  const setSortKey = (key: SortKey) =>
    setSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' },
    );

  const openMenu = (el: HTMLElement, r: SurveyOverviewRowDto) => {
    setMenuAnchor(el);
    setMenuRow(r);
  };
  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  if (isLoading || !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const selectedTermId = data.terms.some((tm) => tm.id === (termId || data.termId))
    ? termId || data.termId || ''
    : '';

  return (
    <Stack spacing={2.5}>
      {/* Period + maintenance/export */}
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="mgmt-term">{t('surveys.overview.period')}</InputLabel>
          <Select
            labelId="mgmt-term"
            label={t('surveys.overview.period')}
            value={selectedTermId}
            onChange={(e) => setTermId(e.target.value)}
          >
            {data.terms.map((tm) => (
              <MenuItem key={tm.id} value={tm.id}>
                {tm.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          startIcon={<RefreshRounded />}
          disabled={backfill.isPending}
          onClick={() => backfill.mutate()}
          title={t('surveys.backfillHint')}
        >
          {backfill.isPending ? t('surveys.backfillRunning') : t('surveys.backfill')}
        </Button>
        {rows.length > 0 && (
          <Button size="small" startIcon={<DownloadRounded />} onClick={() => exportCsv(data)}>
            {t('surveys.exportCsv')}
          </Button>
        )}
      </Stack>

      {/* KPIs */}
      <Stack direction="row" spacing={2} flexWrap="wrap">
        <Stat label={t('surveys.overview.totalSurveys')} value={String(data.totalSurveys)} />
        <Stat label={t('surveys.kpi.open')} value={String(openCount)} />
        <Stat
          label={t('surveys.overview.avgRate')}
          value={`${Math.round(data.avgResponseRate * 100)}%`}
        />
        <Stat
          label={t('surveys.overview.redFlags')}
          value={String(data.redFlags)}
          alert={data.redFlags > 0}
        />
      </Stack>

      {/* Action bar: launch + filters */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
        <Button variant="contained" startIcon={<AddRounded />} onClick={() => setLaunchOpen(true)}>
          {t('surveys.newSurvey')}
        </Button>
        <Box sx={{ flex: 1 }} />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={statusFilter}
          onChange={(_, v) => v && setStatusFilter(v)}
        >
          <ToggleButton value="all">{t('surveys.filter.all')}</ToggleButton>
          <ToggleButton value="open">{t('surveys.status.open')}</ToggleButton>
          <ToggleButton value="closed">{t('surveys.status.closed')}</ToggleButton>
          <ToggleButton value="draft">{t('surveys.status.draft')}</ToggleButton>
        </ToggleButtonGroup>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="mgmt-course">{t('surveys.col.course')}</InputLabel>
          <Select
            labelId="mgmt-course"
            label={t('surveys.col.course')}
            value={courseFilter}
            onChange={(e) => setCourseFilter(e.target.value)}
          >
            <MenuItem value="all">{t('surveys.filter.all')}</MenuItem>
            {courseOptions.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel id="mgmt-kind">{t('surveys.col.type')}</InputLabel>
          <Select
            labelId="mgmt-kind"
            label={t('surveys.col.type')}
            value={kindFilter}
            onChange={(e) => setKindFilter(e.target.value as KindFilter)}
          >
            <MenuItem value="all">{t('surveys.filter.all')}</MenuItem>
            <MenuItem value="periodic">{t('surveys.kind.periodic')}</MenuItem>
            <MenuItem value="end_of_course">{t('surveys.kind.end_of_course')}</MenuItem>
          </Select>
        </FormControl>
        <TextField
          size="small"
          placeholder={t('surveys.filter.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRounded fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      {/* Global table */}
      {rows.length === 0 ? (
        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography color="text.secondary">{t('surveys.overview.empty')}</Typography>
          </CardContent>
        </Card>
      ) : visibleRows.length === 0 ? (
        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography color="text.secondary">{t('surveys.filter.noMatch')}</Typography>
          </CardContent>
        </Card>
      ) : (
        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortCell
                  k="course"
                  label={t('surveys.col.course')}
                  sort={sort}
                  onSort={setSortKey}
                />
                <SortCell
                  k="title"
                  label={t('surveys.col.title')}
                  sort={sort}
                  onSort={setSortKey}
                />
                <SortCell k="kind" label={t('surveys.col.type')} sort={sort} onSort={setSortKey} />
                <SortCell
                  k="status"
                  label={t('surveys.col.status')}
                  sort={sort}
                  onSort={setSortKey}
                />
                <SortCell
                  k="rate"
                  label={t('surveys.col.responses')}
                  sort={sort}
                  onSort={setSortKey}
                />
                <SortCell
                  k="health"
                  label={t('surveys.col.health')}
                  sort={sort}
                  onSort={setSortKey}
                  align="right"
                />
                <SortCell
                  k="window"
                  label={t('surveys.col.window')}
                  sort={sort}
                  onSort={setSortKey}
                />
                <TableCell align="right">{t('surveys.col.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((r) => (
                <TableRow key={r.surveyId} hover>
                  <TableCell>
                    <Typography variant="body2">{r.courseName ?? r.sectionId}</Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 240 }}>
                    <Typography variant="body2" fontWeight={600} noWrap title={r.title}>
                      {r.title}
                    </Typography>
                    {r.actionNote && (
                      <Tooltip title={r.actionNote}>
                        <Chip
                          size="small"
                          variant="outlined"
                          color="info"
                          icon={<FactCheckRounded />}
                          label={t('surveys.actionRecorded')}
                          sx={{ mt: 0.5 }}
                        />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {t(`surveys.kind.${r.kind}`)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={t(`surveys.status.${r.status}`)}
                      color={r.status === 'open' ? 'success' : 'default'}
                      variant={r.status === 'open' ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 130 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ flex: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={Math.round(r.responseRate * 100)}
                          sx={{ height: 6, borderRadius: 1 }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {r.responseCount}/{r.eligibleCount}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    {r.health === 'na' ? (
                      <Typography variant="caption" color="text.secondary">
                        —
                      </Typography>
                    ) : (
                      <Chip
                        size="small"
                        color={HEALTH_COLOR[r.health]}
                        label={t(`surveys.analytics.health.${r.health}`)}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {r.opensAt ? r.opensAt.slice(0, 10) : '—'}
                      {r.closesAt ? ` → ${r.closesAt.slice(0, 10)}` : ''}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('surveys.viewResults')}>
                      <IconButton
                        size="small"
                        onClick={() => setAnalyticsSurvey({ surveyId: r.surveyId, title: r.title })}
                      >
                        <InsightsRounded fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('surveys.diag.button')}>
                      <IconButton
                        size="small"
                        onClick={() => setDiagSurvey({ surveyId: r.surveyId, title: r.title })}
                      >
                        <TroubleshootRounded fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <IconButton size="small" onClick={(e) => openMenu(e.currentTarget, r)}>
                      <MoreVertRounded fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Row overflow menu */}
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={closeMenu}>
        {menuRow?.status === 'open' && [
          <MenuItem
            key="remind"
            disabled={remindMutation.isPending}
            onClick={() => {
              if (menuRow) remindMutation.mutate(menuRow.surveyId);
              closeMenu();
            }}
          >
            <ListItemIcon>
              <NotificationsActiveRounded fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('surveys.remind')}</ListItemText>
          </MenuItem>,
          <MenuItem
            key="close"
            disabled={closeMutation.isPending}
            onClick={() => {
              if (menuRow) closeMutation.mutate(menuRow.surveyId);
              closeMenu();
            }}
          >
            <ListItemIcon>
              <StopCircleRounded fontSize="small" color="warning" />
            </ListItemIcon>
            <ListItemText>{t('surveys.close')}</ListItemText>
          </MenuItem>,
        ]}
        {menuRow?.status === 'closed' && (
          <MenuItem
            onClick={() => {
              if (menuRow) setAnalyticsSurvey({ surveyId: menuRow.surveyId, title: menuRow.title });
              closeMenu();
            }}
          >
            <ListItemIcon>
              <FactCheckRounded fontSize="small" color="info" />
            </ListItemIcon>
            <ListItemText>{t('surveys.recordAction')}</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setDeleteTarget(menuRow);
            closeMenu();
          }}
        >
          <ListItemIcon>
            <DeleteOutlineRounded fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>{t('surveys.delete.action')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Launch drawer */}
      {currentTenantId && (
        <SurveyLaunchDrawer
          open={launchOpen}
          tenantId={currentTenantId}
          templates={templates}
          onClose={() => setLaunchOpen(false)}
          onLaunched={(msg) => {
            invalidate();
            setSnack({ severity: 'success', msg });
          }}
        />
      )}

      {/* Dialogs */}
      {analyticsSurvey && (
        <SurveyAnalyticsDialog
          surveyId={analyticsSurvey.surveyId}
          title={analyticsSurvey.title}
          onClose={() => setAnalyticsSurvey(null)}
        />
      )}
      {diagSurvey && currentTenantId && (
        <SurveyDiagnosticsDialog
          tenantId={currentTenantId}
          surveyId={diagSurvey.surveyId}
          title={diagSurvey.title}
          onClose={() => setDiagSurvey(null)}
        />
      )}

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('surveys.delete.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {t('surveys.delete.confirm', { title: deleteTarget?.title ?? '' })}
            {(deleteTarget?.responseCount ?? 0) > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {t('surveys.delete.warnResponses', { count: deleteTarget?.responseCount ?? 0 })}
              </Alert>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteMutation.isPending}>
            {t('actions.cancel', { ns: 'common' })}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteMutation.isPending}
            onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.surveyId)}
          >
            {deleteMutation.isPending ? t('surveys.delete.deleting') : t('surveys.delete.action')}
          </Button>
        </DialogActions>
      </Dialog>

      {snack && (
        <Snackbar
          open
          autoHideDuration={4000}
          onClose={() => setSnack(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={snack.severity} onClose={() => setSnack(null)} variant="filled">
            {snack.msg}
          </Alert>
        </Snackbar>
      )}
    </Stack>
  );
}

function SortCell({
  k,
  label,
  sort,
  onSort,
  align,
}: {
  k: SortKey;
  label: string;
  sort: { key: SortKey; dir: 'asc' | 'desc' };
  onSort: (k: SortKey) => void;
  align?: 'right';
}): JSX.Element {
  return (
    <TableCell {...(align ? { align } : {})} sortDirection={sort.key === k ? sort.dir : false}>
      <TableSortLabel
        active={sort.key === k}
        direction={sort.key === k ? sort.dir : 'asc'}
        onClick={() => onSort(k)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );
}

function Stat({ label, value, alert }: { label: string; value: string; alert?: boolean }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 3, minWidth: 120, flex: 1 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h4" fontWeight={800} color={alert ? 'error.main' : 'text.primary'}>
            {value}
          </Typography>
          {alert && <WarningAmberRounded color="error" fontSize="small" />}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </CardContent>
    </Card>
  );
}

function exportCsv(data: SurveyOverviewDto): void {
  const rows: (string | number)[][] = [
    ['Período', data.termName ?? ''],
    [],
    ['Encuesta', 'Curso', 'Tipo', 'Estado', 'Respuestas', 'Alumnos', 'Tasa', 'Favorable', 'Salud'],
  ];
  for (const r of data.rows) {
    rows.push([
      r.title,
      r.courseName ?? '',
      r.kind,
      r.status,
      r.responseCount,
      r.eligibleCount,
      `${Math.round(r.responseRate * 100)}%`,
      r.favorableShare !== undefined ? `${Math.round(r.favorableShare * 100)}%` : '',
      r.health,
    ]);
  }
  downloadCsv(`encuestas-periodo-${data.termName ?? 'actual'}`, rows);
}
